// THE COMPLETENESS CLAIM, in one place.
//
// Key parity has been gated since PR #9 (D-014), and key parity is exactly the
// property a placeholder locale satisfies perfectly: `ru = en` has flawless
// parity and is unusable in Almaty. This file states, per locale, WHICH parts of
// the dictionary carry real translations — and tools/ci/i18n-parity.ts turns the
// claim into a build failure if it stops being true.
//
// It is deliberately a claim rather than a measurement. A gate that inferred
// completeness ("ru differs from en, therefore ru is done") would go green on a
// half-finished namespace and on machine slop alike. Someone has to assert that
// a namespace was translated; this file is where they sign it, and the gate's
// job is to stop the assertion from rotting.
//
// UPDATING THIS FILE IS THE POINT. Translating a namespace means moving it from
// `outstanding` to `complete` here, in the same commit — and the gate then holds
// you to it forever after.

import { SUPPORTED_LOCALES, type Locale } from "@assessly/contracts/locales";
import { COMMERCIAL_IDENTICAL } from "./i18n-identical/commercial.js";
import { SHELL_IDENTICAL } from "./i18n-identical/shell.js";
import { CORE_NAMESPACES } from "./locales/core-namespaces.js";
import { OPERATIONS_IDENTICAL } from "./i18n-identical/operations.js";
import { AGENTS_IDENTICAL } from "./i18n-identical/agents.js";

// The registry's locales (D-430). Read from the registry rather than from
// i18n-dictionaries.ts so that the runtime, which reads this file to decide
// what the switcher offers, never imports every locale's dictionary.
const LOCALES = SUPPORTED_LOCALES;

/**
 * Dotted key PREFIXES that carve the dictionary into declarable units. Longest
 * match wins, so `inventory.floor` can be tracked separately from the rest of
 * `inventory` — the floor PWA and the office area have different audiences and
 * were translated in a deliberate order.
 *
 * The gate asserts this list COVERS every leaf key, so a new top-level namespace
 * cannot appear without someone classifying it.
 */
export const NAMESPACES = [
  "app",
  "nav",
  "home",
  "login",
  // The password-reset pair (UX-6a): routes/forgot-password.tsx +
  // reset-password.tsx. Split from `login` because the reset screens are
  // reached from an EMAIL, not from the app — their copy is written to stand
  // alone — but translated in the same breath (ru declares ALL complete).
  "auth",
  // `products` / `newProduct` are GONE, not outstanding: they carved out the P0
  // walking-skeleton list and create form, and those screens are retired (their
  // paths redirect into `/catalogue/products`). The module's own strings are in
  // `catalogue`.
  "kind",
  "status",
  // The designed 404 (routes/not-found.tsx) and the SYSTEM ROLE LABELS. The ten
  // seeded roles carry an i18n key as their DB `name`, so `roles.*` is a real
  // user-facing namespace even though nothing in `apps/web` references it with
  // a literal key — see lib/role-label.ts and its exhaustiveness test.
  "notFound",
  "roles",
  // `components/ref-label.tsx` — the two accessible names for a reference that
  // is NOT a resolved name ("Nothing recorded", "Looking up the name…"). Both
  // are the only text a one-glyph rendering (— / …) offers a screen reader, so
  // an untranslated one is a cell that says nothing in Russian.
  "ref",
  // `routes/shell-data.ts` — the two forms a COUNT takes when the only
  // affordable read is one page of a cursor-paginated list: a true lower bound
  // ("200+") and "we could not read this" (—). App-wide like `ref`: the sidebar
  // and the home KPI strip both paint them.
  "count",
  // `components/evidence-photos.tsx` — the attachment strip. Its four states
  // are the only words attached to a picture (or to the frame where one could
  // not be shown), so an untranslated one is a photo nobody can caption.
  "evidence",
  // `lib/quantity.ts` `unitSymbol` — the symbol a reader's language writes for
  // a unit code, so "ohm_per_km" reads "Ω/km" and "pcs" reads "шт." (design-system §3.9).
  "unit",
  "catalogue",
  "onboarding",
  "inventory",
  "inventory.floor",
  "waste",
  "production",
  // Module 07 §4 (slice 07-A): the control-point library and editor, the
  // inspection queue, and the ONE execution screen both shells host. Its floor
  // strings live under `quality.floor.*` inside this namespace rather than in a
  // second one — the audience is the same inspector on both shells, and §4.4's
  // "office fallback identical" would be a lie if the two read differently.
  "quality",
  // Module 09 §4 (slice P3-A part 2): the office SALES area — the customer
  // book, the customer 360 and the price-list manager with its off-cut
  // discount and margin-guard editors. Its reader is a KazKabel sales manager
  // quoting cable to a Kazakh distributor, so an English-only screen here is a
  // screen the design partner's commercial department cannot work in. ru is
  // translated with it (areas/office/sales/sales.ru.ts).
  "sales",
  // Module 17 §4 (slice P3-B part 2): the office MESSAGING area — the
  // conversation inbox with its thread and context panes, the message-template
  // catalog, the ga/fa settings screen, and the §4.5 link widget the customer
  // 360 embeds. Its reader is the same KazKabel sales manager as `sales`,
  // answering ЭлектроМонтаж in Russian from this inbox — so ru ships
  // translated with the area (areas/office/messaging/messaging.ru.ts).
  "messaging",
  // Module 09 §4.10 (slice P3-C-1): the two PUBLIC customer-facing pages — the
  // tokenized quote-accept page and the order-tracking page. This is the ONE
  // namespace whose reader is not an employee of the tenant at all: it is the
  // purchasing manager at a Kazakh distributor, opening a Telegram link on a
  // phone. Its language is chosen by the QUOTE's `locale`, not by the browser
  // or a session — so an untranslated namespace here does not degrade to "the
  // reader switches to English", it hands a Russian customer an English page
  // they did not ask for and cannot change. ru ships translated with it
  // (areas/public/public.ru.ts).
  //
  // ONE PREFIX, NOT TWO. This namespace was briefly mounted at BOTH `pub` and
  // `public` in i18n-dictionaries.ts — the same object under two keys, with
  // every screen reading `public.*` and nothing reading `pub.*`. That is not a
  // harmless alias: parity passes vacuously on the unread prefix, so the day
  // somebody edits one dictionary through the other name the gate stays green
  // while the pages go untranslated. The alias is deleted.
  "public",
  // The AUDIT TRAIL (spec 16 §2.16/§4) — the office read surface over the
  // hash-chained `audit_log`. Its reader is the Big Boss, and its output is what
  // gets shown to an auditor or quoted in a dispute, so an English-only screen
  // here is a compliance record the design partner cannot actually use. ru is
  // translated with it.
  "audit",
  // The platform console (spec 15 §4) — admin.<apex>. Its audience is Assessly
  // staff, not a factory, but the locale switcher is on its header like
  // everywhere else and a half-translated area is exactly what this file exists
  // to stop.
  "admin",
  // SAVED VIEWS (spec 16 §4, D-305): the switcher, Save as new, Manage, the
  // apply-only list and the column chooser on every list that adopts saved
  // views, today the three sales books and the work-order list. Their readers
  // are the KazKabel sales managers and planners who work those lists in
  // Russian, so ru ships translated with the feature
  // (areas/office/views/views.ru.ts).
  "views",
  // Module 03 §4 (D-354): the planning area, its forecast editor and the
  // BucketGrid's words. KazKabel's planners work in Russian, so ru ships
  // translated with it (areas/office/planning/planning.ru.ts).
  "planning",
  // Module 14 §4.2 to §4.4 (P3-K K-2, D-529 ruling 2, D-538 ruling 1): the
  // Agent Hub, the agent detail with its policy editor and prompt tab, and the
  // action journal, plus the tool names and undo instructions every card reads.
  // The design partner's Big Boss decides agents' asks from his phone in
  // Russian, so ru ships translated with it (areas/office/agents/agents.ru.ts).
  "agents",
] as const;

export type Namespace = (typeof NAMESPACES)[number];

/**
 * How a complete namespace was checked (D-430). `machine`: translated by a
 * model against the glossary and back-translated, with no native speaker's
 * sign-off yet. `native`: reviewed by a native speaker. A machine-reviewed
 * locale may be selectable; native review of the floor, public, email,
 * Telegram and document text is required before a customer goes live in it.
 */
export type Review = "machine" | "native";

export interface LocaleCompleteness {
  /**
   * `source`      — the authoritative locale; nothing to translate.
   * `translated`  — every namespace in `complete`; `outstanding` must be empty.
   * `placeholder` — not offered: English text is still shipping for the
   *                 namespaces listed in `outstanding`, or every namespace is
   *                 translated and the locale waits for the back-translation
   *                 record that promotion needs (D-438 ruling 6). Said out
   *                 loud, not implied by silence, and never selectable
   *                 (lib/locale.ts SELECTABLE_LOCALES). No locale holds it
   *                 since D-440 promoted fr, zh, zh-Hant and hi; it stays the
   *                 standing of a locale the registry adds before its
   *                 translation (D-440 ruling 5).
   */
  readonly status: "source" | "translated" | "placeholder";
  readonly complete: readonly Namespace[];
  readonly outstanding: readonly Namespace[];
  /** One entry per `complete` namespace of a non-source locale, and no other
   * (the gate holds it to that). */
  readonly review: Readonly<Partial<Record<Namespace, Review>>>;
  readonly note: string;
}

function reviewed(
  namespaces: readonly Namespace[],
  review: Review,
): Partial<Record<Namespace, Review>> {
  return Object.fromEntries(namespaces.map((ns) => [ns, review]));
}

const ALL: readonly Namespace[] = NAMESPACES;

/**
 * The namespaces machine-translated into all four of fr, zh, zh-Hant and hi
 * (P3-UX L5). Each is written from English against docs/i18n/glossary.json,
 * one `<area>/<ns>.<locale>.ts` file per locale. Every namespace is on this
 * list since P3-UX batch 3, and every key of every namespace has a
 * back-translation record under docs/i18n/review-queue/ since D-440, which
 * promoted the four locales to `translated` (D-438 ruling 6).
 */
const MACHINE_TRANSLATED: readonly Namespace[] = [
  // Track TR1: the shell's core, read from CORE_NAMESPACES so that a namespace
  // moved into src/locales/core.<locale>.ts is claimed, and value-checked, in
  // the same edit, and the commercial areas.
  ...CORE_NAMESPACES,
  "sales",
  "messaging",
  // Track TR2.
  "public",
  "audit",
  "views",
  "admin",
  "catalogue",
  "onboarding",
  "inventory",
  // `inventory.floor` is its own namespace (the longest prefix wins), so
  // claiming `inventory` alone left TR2's floor stock screens unchecked.
  "inventory.floor",
  // Track TR3: the office home, the floor-facing production, quality and waste
  // areas, and planning.
  "home",
  "waste",
  "production",
  "quality",
  "planning",
  // P3-K K-2 (D-538 ruling 1): the agents area, translated and back-translated
  // with the screens (docs/i18n/review-queue/bt/<locale>/p3k-agents-1.json),
  // because all four locales are `translated` since D-440 and an untranslated
  // namespace would take them out of the switcher.
  "agents",
];

function untranslated(done: readonly Namespace[]): readonly Namespace[] {
  return ALL.filter((ns) => !done.includes(ns));
}

export const LOCALE_COMPLETENESS: Readonly<Record<Locale, LocaleCompleteness>> = {
  en: {
    status: "source",
    complete: ALL,
    outstanding: [],
    review: {},
    note: "Authoritative. Every other locale is checked against this key set.",
  },
  ru: {
    status: "translated",
    complete: ALL,
    outstanding: [],
    // Written for the design partner without a native speaker's sign-off yet;
    // docs/i18n/ru-translation-notes.md lists the ten terms flagged for one.
    review: reviewed(ALL, "machine"),
    note:
      "Translated 2026-08-03 for the design partner (KazKabel, Almaty), whose office staff and " +
      "floor operators work in Russian. Industrial register, not generic UI Russian: приход/расход/" +
      "сторно for the ledger, отрезок/ширина реза/перемер at the drum, пересчёт/расхождение for " +
      "counting. Entity nouns are NOT here — they resolve through the terminology layer " +
      "(TERMINOLOGY_BASE.ru, module 02 §2.5), which this dictionary is pinned against.",
  },
  zh: {
    status: "translated",
    complete: MACHINE_TRANSLATED,
    outstanding: untranslated(MACHINE_TRANSLATED),
    review: reviewed(MACHINE_TRANSLATED, "machine"),
    note:
      "Machine-translated against docs/i18n/glossary.json and back-translated key by key " +
      "(docs/i18n/review-queue/bt/zh, D-440), so it is offered. Native review by a mainland " +
      "plant reader is pending (D-430 ruling 11).",
  },
  fr: {
    status: "translated",
    complete: MACHINE_TRANSLATED,
    outstanding: untranslated(MACHINE_TRANSLATED),
    review: reviewed(MACHINE_TRANSLATED, "machine"),
    note:
      "Machine-translated and back-translated key by key (docs/i18n/review-queue/bt/fr, " +
      "D-440), so it is offered. Native review by a reader from the Francophone launch markets " +
      "is pending and first in line (D-430 ruling 11).",
  },
  "zh-Hant": {
    status: "translated",
    complete: MACHINE_TRANSLATED,
    outstanding: untranslated(MACHINE_TRANSLATED),
    review: reviewed(MACHINE_TRANSLATED, "machine"),
    note:
      "Traditional Chinese with Taiwan vocabulary, written from English with " +
      "docs/i18n/glossary.json and never converted from `zh`, and back-translated key by key " +
      "(docs/i18n/review-queue/bt/zh-hant, D-440), so it is offered. Native review by a Taiwan " +
      "reader is pending. Server text (email, Telegram, PDFs) is written in it too (D-590).",
  },
  hi: {
    status: "translated",
    complete: MACHINE_TRANSLATED,
    outstanding: untranslated(MACHINE_TRANSLATED),
    review: reviewed(MACHINE_TRANSLATED, "machine"),
    note:
      "Hindi in Devanagari with Latin digits, as Indian plants speak it " +
      "(docs/i18n/glossary.json), back-translated key by key (docs/i18n/review-queue/bt/hi, " +
      "D-440), so it is offered. Native review by an Indian cable-plant reader is pending. " +
      "Server text (email, Telegram, PDFs) is written in it too (D-590).",
  },
};

/**
 * Keys whose translated value is legitimately IDENTICAL to English, so the
 * value gate must not read them as untranslated placeholders. Every entry is
 * one of five things and nothing else:
 *
 *   1. a pure interpolation passthrough — the whole value is `{{placeholder}}`,
 *      and the text arrives already translated from the terminology layer;
 *   2. a symbol or punctuation cell — `—`, `Δ%`, `{{pct}}%`;
 *   3. a proper noun — the product's own name — or an acronym the glossary
 *      keeps in English in that language (VAT and SKU in Hindi, PDF, CSV);
 *   4. a literal example of machine syntax the user types verbatim;
 *   5. a word the target language writes exactly as English does, with the
 *      same meaning on that screen: a glossary term listed `same_as_en` for
 *      that locale in docs/i18n/glossary.json (fr Total, hi VAT), or a word
 *      ordinary usage of that language spells the same way (fr Production,
 *      Type, Code). In a locale with another script that is only an acronym
 *      it keeps in Latin letters (hi VAT, URL). Never a word left in English
 *      because nobody translated it (D-430 ruling 14).
 *
 * This list is per-locale on purpose: a string that is legitimately identical in
 * Russian may need translating in French.
 *
 * Adding an entry here is how you tell the gate "this one is fine". Adding one
 * that is really just untranslated English defeats the gate, so each is
 * justified by the categories above and nothing else qualifies.
 */
export const IDENTICAL_BY_DESIGN: Readonly<Record<Locale, readonly string[]>> = {
  en: [],
  ru: [
    // 3 — the product's own name.
    "app.title",
    ...AGENTS_IDENTICAL.ru,
    // Category 1 (D-560): the demand pool's filter chip is the filter's name
    // and its value, both already translated, with a colon.
    "planning.demand.filterChip",
    // Category 1, a pure interpolation cell: the grid cell's accessible name is the
    // product and the week, both data, with a separator.
    "planning.grid.cell",
    // Category 1, pure interpolation cells of the MRP screens (P3-H-2): who and
    // when a planned order was firmed, and a product beside the reason MRP left
    // it unplanned, both data with a separator.
    "planning.orders.drawer.byAt",
    // Category 1 (P3-J J-3): the promises tile's "at least" count and a next
    // promise's number beside its date, data with a symbol or separator.
    "planning.promises.tile.atLeast",
    "planning.promises.tile.next",
    "planning.mrpRun.entry.unplanned",
    // Category 1: a work order's number beside the reason MRP left its
    // component demand out of the run, both data with a separator (D-407).
    "planning.mrpRun.entry.skippedOrder",
    // Category 1, pure interpolation cells of the schedule board (P3-I-2): a
    // step's number beside its name, and a block's changeover minutes with
    // the minute mark, both data with a separator or a symbol.
    "planning.schedule.drawer.stepText",
    "planning.schedule.block.setupShort",
    // 3 — MRP is the planning method's own name, written in Latin letters in
    // Russian planning practice as it is in English, and the workbench section
    // and its tile carry that name alone, as does the settings card for it.
    "planning.mrp.title",
    "planning.mrp.tiles.mrp",
    "planning.settings.mrpTitle",
    // "PDF" is the universal file-format abbreviation in Russian technical
    // usage too (ГОСТ documents say PDF, not ПДФ) — 07-E's §4.7 action label.
    "quality.coa.pdf",
    // 3 — the product's own name, split across the shell's brand block (UX-0).
    "nav.brandName",
    "nav.brandSub",
    // 2 — a numeral and a symbol, and nothing else. `count.atLeast` is the
    // OVERFLOW form a count slot renders when the page came back with a cursor
    // ("200+" — a true lower bound, not a rounded number), and `count.unknown`
    // is the em-dash for a count that could not be read at all. Both are read
    // identically by a Russian storekeeper; the worded Russian form
    // («не менее 200») is correct and does not fit a 216 px nav row.
    "count.atLeast",
    "count.unknown",
    // 2 — symbol / punctuation cells.
    "catalogue.cost.none",
    "catalogue.costRun.colDelta",
    // 2 — the Δ% threshold options in S7's filter Select (UX-2): a comparison
    // operator, an interpolated number and a percent sign. There is no word in
    // it, and "≥ 5%" is what a Russian finance lead reads too.
    "catalogue.costRun.minDeltaOption",
    "onboarding.importDetail.meta.none",
    "onboarding.importDetail.rows.messagesNone",
    "onboarding.vertical.drawer.steps.scrapPct",
    "inventory.atp.noDrill",
    // 2 — a symbol cell: the percentage sign after an interpolated number.
    "waste.settings.pct",
    // 2 — a symbol cell: the diameter sign heading S5's Ø column (05-B).
    "waste.remnants.colDiameter",
    // 2 — a symbol cell: the em-dash S13's Pareto prints where a SHARE cannot
    // be computed at all (the period's total is zero, so "0%" would be a claim
    // rather than a measurement). Same reasoning as `production.common
    // .notAvailable` below, and the same glyph in every language.
    "waste.pareto.shareUnknown",
    // 2 — a symbol cell: the em-dash an empty production cell renders (a null
    // due date, a not-yet timestamp — NEVER a redacted money field, which is
    // ABSENT rather than dashed, spec 04 §7).
    "production.common.notAvailable",
    // 2 — the same em-dash, on the two 04-C-4 surfaces where a shift produced
    // NOTHING and therefore has no first-pass yield. Null yield is not 0%, and
    // a dash is the one rendering that says so in every language.
    "production.shifts.noYield",
    "production.floor.wall.noYield",
    // 2 — a pure interpolation cell (04-C-5): the hand-off ledger renders a
    // shift as `CODE · DATE`, and both halves are DATA (the §2.9 code the
    // tenant typed, and an ISO date). There is no word here to translate, and
    // inventing one would put an English label in front of a Russian
    // supervisor's shift code.
    "production.shifts.handoffShiftCell",
    // 1 — pure interpolation passthrough: the shift CODE (tenant data, already
    // in the tenant's own words) and its date, with a separator.
    "production.floor.myShift.shiftLine",
    // 1 — the same shape on module 05's kiosk board: the FACILITY NAME (tenant
    // data — `/v1/org/facilities` carries no `name_i18n`, so it is whatever the
    // tenant typed) and an ISO business date, with a separator. Nothing here is
    // a word, and putting an English label in front of a Russian plant's own
    // name on a wall board would be worse than the separator.
    "waste.shifts.kioskContext",
    // Deliberately blank: a spacer column header with no accessible label of
    // its own (the state chip beside it carries the meaning).
    "onboarding.importDetail.mapping.colState",
    // 1 — machine syntax the user copies verbatim. (The S1 attribute-filter
    // placeholder used to sit here too; UX-2 replaced that free-text DSL with a
    // structured key/comparison/value control, so there is no raw syntax left
    // on that screen to exempt.)
    "catalogue.uom.reciprocal",
    // 1 — pure interpolation passthroughs. The value IS the placeholder; the
    // text inside it is a terminology label that arrives already localised.
    "onboarding.importShared.fields.facility_id",
    "onboarding.importShared.fields.work_center",
    "onboarding.catalogueStep.dropzone.chosen",
    "onboarding.importDetail.meta.facility",
    "onboarding.vertical.drawer.qc.range",
    "onboarding.plant.facilityLabel",
    // 2 — punctuation cells of the UX-1 commit sentence: the list separator is
    // the same "," glyph in ru (the conjunction "и" IS translated, beside it).
    "onboarding.plant.sentence.comma",
    // 1 — pure interpolation with a multiplication sign: "{{count}} ×
    // {{entity}}" — the entity arrives already localised via terminology.
    "onboarding.plant.resultPart",
    "onboarding.plant.absentKind.facility",
    "onboarding.plant.absentKind.department",
    "onboarding.plant.absentKind.work_center",
    "onboarding.plant.bindings.facilityLabel",
    "onboarding.plant.people.facilityLabel",
    "inventory.lookup.groups.units",
    "inventory.unitCard.lastScanValue",
    "inventory.atp.asOf",
    // 3 — the product's own name, in the platform console's header block.
    "admin.brand",
    // 2 — a symbol cell: the em-dash the strength meter shows for an empty
    // password field, before there is anything to judge.
    "admin.accept.strength.none",
    // M15-4: an unread health figure is a dash in every language.
    "admin.health.none",
    // 2 — symbol cells (07-A): the em-dash a queue row renders for "this
    // inspection is not finished / blocks nothing / names no subject". Never a
    // redacted money field — spec 07 §7 follows D-074 pt 3 and redaction is
    // ABSENCE, so a dash here can only ever mean "there is no such thing".
    "quality.queue.notCompleted",
    "quality.queue.notBlocking",
    "quality.queue.noSubject",
    // 2 — the same symbol cells on the Quarantine Board (07-B-3): "no agent
    // proposal on this quarantine" and the unrepresentable no-subject row. The
    // money cell is NOT here — it renders module 05's `•••` mark from code,
    // never from a dictionary value.
    "quality.board.noProposal",
    "quality.board.noSubject",
    // 2 — the same symbol cell for an age the client could not compute from an
    // unparsable `placed_at` (fix round A9): a dash, never "0 d" — zero is a
    // value and an unreadable instant is not that value.
    "quality.board.noAge",
    // 4 — machine syntax the user types verbatim: the checklist-key example in
    // the control-point editor. `checklist_items_i18n` is keyed by a STABLE
    // ASCII key (§2.3) that the CoA and every analytic groups by; translating
    // the example would teach a Russian qc lead to type Cyrillic keys and make
    // the same checklist unjoinable across two tenants' locales.
    "quality.editor.itemChecklistHint",
    // 2 — a symbol cell: the em-dash an audit row renders when it names no
    // entity at all (a pre-auth login failure, a request-level impersonation
    // row). Never a redacted payload — that is a SENTENCE, deliberately, because
    // "not shown to your role" and "there is no such thing" are different facts.
    "audit.row.noEntity",
    // 3 — proper nouns and an industry token that is the SAME token in
    // Russian commercial usage: "B2B" is what a Kazakh price list itself says
    // («категория B2B»), and Telegram/WhatsApp are the products' own names —
    // the two channels a customer is actually reached on.
    "sales.tier.b2b",
    "sales.channel.telegram",
    "sales.channel.whatsapp",
    // 2 — symbol cells: an interpolated number and a percent sign, nothing
    // else. A margin, a discount and a guard threshold read identically in
    // Russian; the WORD beside each of them is translated where it appears
    // (`colDiscount`, `colMinMargin`, `priceCheck.marginPct`).
    "sales.offcut.pct",
    "sales.guards.pct",
    // 1 — §4.9's "no rate" cell: an em dash, and only an em dash. A rate
    // with no denominator is not 0% (a manager who sent nothing has no win
    // rate), so the cell renders the symbol rather than a number — and the
    // SENTENCE that explains it (`analytics.noRateHint`) is translated.
    "sales.analytics.noRate",
    // 1 — the composer's per-line margin cell: an interpolated number and a
    // percent sign, exactly like the two above it. "9.00%" is what a Russian
    // sales manager reads too.
    "sales.composer.marginPct",
    "sales.priceListDetail.marginPct",
    // 1 — pure interpolation passthroughs: an exact-SKU picker's applied
    // caption is the tenant's own SKU and the tenant's own product name with a
    // separator between them. There is no word here to translate, and putting
    // an English label in front of a Russian plant's own article number would
    // be worse than the separator.
    "sales.eligible.skuApplied",
    "sales.priceCheck.productApplied",
    "sales.priceListDetail.skuApplied",
    "sales.guards.productApplied",
    // 3 — the same shape for P3-E2 PR E2-1's pipeline: a deal's own number and
    // title with a separator (the composer's Deal chip), a task kind and a
    // date with a separator (the next-step chip, whose kind word is translated
    // in `sales.tasks.kind.*`), and a peek panel's accessible name, which is
    // the translated record kind (`sales.peek.kind.*`) beside the record's own
    // number or name.
    "sales.composer.dealChip",
    "sales.nextStep.on",
    "sales.peek.title",
    // 1 — pure interpolation (P3-E2 PR E2-2a1, spec 09 §4.2): a note's author
    // and its date, "{{name}}, {{date}}". The name is the member's own and the
    // date arrives already formatted in the reader's locale, so the comma is
    // the only glyph, and it is the same in ru.
    "sales.notes.byOn",
    // 1 — pure interpolation (P3-E2 PR E2-2d, D-326 ruling 1): one refused
    // tag in a Tags cell's refusal, "{{tag}} ({{reason}}, {{code}})". The tag's
    // name is the tenant's own, the reason arrives already translated
    // (`sales.bulk.reason.*`) and the code is the server's token, so the
    // brackets and the comma are the only glyphs, and they are the same in ru.
    // The sentence that lists them (`sales.cells.tags.refused`) is translated.
    "sales.cells.tags.refusedItem",
    "sales.cells.tags.refusedCode",
    // 4 — machine syntax the user types (or pastes) verbatim: the header line
    // an import file must carry. Translating `sku`/`uom`/`price` would teach a
    // Russian sales manager to write column names the parser refuses.
    "sales.import.pasteHint",
    // 3 — the two providers' own names (module 17): what the inbox rows, the
    // settings cards and the §4.5 link widget call the channels — the same
    // words a Russian sales office already says, and the sales.channel.*
    // precedent directly above this block.
    "messaging.provider.telegram",
    "messaging.provider.whatsapp",
    // 4 — a machine token (module 17): the WA button-URL field label. "URL"
    // is what a Russian template editor's own field says (ГОСТ-style Russian
    // technical usage keeps the Latin abbreviation; «адрес» would collide
    // with the identity block's АДРЕС, a chat address).
    "messaging.templates.buttonUrlField",
    // 2 — a counter cell (module 17): two interpolated numbers and a slash.
    // The word beside it («Текст», the body label) is translated where it
    // appears; the counter itself reads identically in Russian.
    "messaging.templates.counter",
    // 1 — pure interpolation (P3-E2 PR E2-2e, D-327 ruling 20): an email
    // sender as a holder of the address grant reads it, "{{name}}
    // <{{address}}>". The name and the address are the sender's own, and the
    // angle brackets are the form a From line takes in every mail client, in
    // Russian as in English.
    "messaging.email.senderNamed",
    // 4 — machine syntax: the FILENAME of the downloaded recovery-code file.
    // Deliberately ASCII in every locale — it is typed into shells, attached to
    // tickets, and synced by password managers, and a Cyrillic filename is a
    // mojibake risk for a file whose whole job is to be findable later. The
    // file's HEADING inside it (`admin.recovery.fileHeader`) IS translated.
    "admin.recovery.fileName",
    // P3-J J-4 (D-474): the what-if screens. Categories 1 and 2: a percentage cell; pure interpolation, base and scenario with an arrow; pure interpolation, who and when.
    "planning.scenarios.created",
    "planning.scenarios.result.kpiSub",
    "planning.scenarios.result.pct",
  ],
  fr: [
    ...SHELL_IDENTICAL.fr,
    ...COMMERCIAL_IDENTICAL.fr,
    ...OPERATIONS_IDENTICAL.fr,
    ...AGENTS_IDENTICAL.fr,
    // 2 — a symbol or punctuation cell, the same glyph in every language.
    "admin.accept.strength.none",
    // M15-4: an unread health figure is a dash in every language.
    "admin.health.none",
    "audit.row.noEntity",
    "catalogue.cost.none",
    "catalogue.costRun.colDelta",
    "inventory.atp.noDrill",
    "onboarding.importDetail.mapping.colState",
    "onboarding.importDetail.meta.none",
    "onboarding.importDetail.rows.messagesNone",
    "onboarding.plant.sentence.comma",
    // 1 — a pure interpolation cell: the value is placeholders and separators
    // only, and the data arrives already localised.
    "catalogue.productDetail.whereUsed.version",
    "catalogue.routing.leadTime.value",
    "inventory.adjustRequest.lotPart",
    "inventory.atp.asOf",
    "inventory.lookup.groups.units",
    "inventory.unitCard.lastScanValue",
    "onboarding.catalogueStep.dropzone.chosen",
    "onboarding.importDetail.meta.facility",
    "onboarding.importShared.fields.facility_id",
    "onboarding.importShared.fields.work_center",
    "onboarding.plant.absentKind.department",
    "onboarding.plant.absentKind.facility",
    "onboarding.plant.absentKind.work_center",
    "onboarding.plant.bindings.facilityLabel",
    "onboarding.plant.facilityLabel",
    "onboarding.plant.people.facilityLabel",
    "onboarding.plant.resultPart",
    "onboarding.vertical.drawer.qc.range",
    "onboarding.vertical.drawer.steps.setupMinutes",
    // 3 — a proper noun or an acronym the glossary keeps in English in this
    // language (Assessly, VAT, SKU, CSV, PDF, MRP).
    "admin.brand",
    // 4 — machine syntax the user types or reads verbatim.
    "admin.recovery.fileName",
    "catalogue.uom.reciprocal",
    // 5 — a word this language spells exactly as English does, confirmed by
    // docs/i18n/glossary.json or by ordinary usage (fr «Production»,
    // «Catalogue», «Tag»).
    "admin.home.session",
    "admin.nav.label",
    "admin.shell.env.local",
    "admin.shell.env.production",
    "admin.shell.menu",
    "admin.tenant.suspendedMode",
    "admin.tenant.suspension",
    "admin.tenants.page",
    "audit.area.agent",
    "audit.area.catalogue",
    "audit.area.production",
    "catalogue.bomEditor.version",
    "catalogue.costRun.noteLabel",
    "catalogue.floorLookup.card.instructions",
    "catalogue.nav.imports",
    "catalogue.productDetail.boms.version",
    "catalogue.productDetail.details.description",
    "catalogue.productDetail.routings.version",
    "catalogue.routing.columns.actions",
    "catalogue.routing.versionsTitle",
    "catalogue.templates.form.codeLabel",
    "catalogue.templates.source.vertical_pack",
    "catalogue.tracking.lot",
    "catalogue.uom.baseBadge",
    "catalogue.uom.codeLabel",
    "catalogue.uom.colActions",
    "catalogue.uom.colBase",
    "catalogue.uom.colCode",
    "catalogue.uom.conversionsTitle",
    "catalogue.uom.dimension.volume",
    "catalogue.uom.source.vertical_pack",
    "catalogue.version.label",
    "inventory.adjust.unitBase",
    "inventory.common.actions",
    "inventory.countReview.colActions",
    "inventory.counts.colBlind",
    "inventory.cut.serialAuto",
    "inventory.filters.pagination",
    "inventory.floor.count.noteLabel",
    "inventory.floor.op.scan",
    "inventory.locations.code",
    "inventory.lookup.colLot",
    "inventory.movements.colLot",
    "inventory.movements.colType",
    "inventory.movements.type",
    "inventory.transfers.colCode",
    "inventory.vocab.locationKind.production",
    "inventory.vocab.locationKind.zone",
    "inventory.vocab.status.active",
    "onboarding.catalogueStep.jobs.col.actions",
    "onboarding.checklist.categories.catalogue",
    "onboarding.checklist.evidence.attestation",
    "onboarding.checklist.evidence.facilities",
    "onboarding.checklist.evidence.pack_key",
    "onboarding.checklist.module.01_catalogue",
    "onboarding.checklist.module.04_production",
    "onboarding.checklist.module.08_maintenance",
    "onboarding.checklist.module.12_notifications",
    "onboarding.importShared.fields.notes",
    "onboarding.importShared.fields.tags",
    "onboarding.importShared.fields.version",
    "onboarding.importShared.kinds.machines",
    "onboarding.opening.tabs.stock",
    "onboarding.packUpdate.installed.pack",
    "onboarding.packUpdate.installed.version",
    "onboarding.plant.codeLabel",
    "onboarding.plant.people.colActions",
    "onboarding.qualityReview.provenance.pack",
    "onboarding.qualityReview.stage.final",
    "onboarding.screens.importDetail",
    "onboarding.terminology.sourceBadge.base",
    "onboarding.terminology.sourceBadge.pack",
    "onboarding.terms.extrusion",
    "onboarding.terms.lot",
    "onboarding.terms.machine",
    "onboarding.vertical.capture.photo",
    "onboarding.vertical.card.version",
    "onboarding.vertical.drawer.units.colCode",
    "onboarding.vertical.drawer.version",
    "onboarding.vertical.unitKind.volume",
    "views.filters.tag",
    // P3-J J-4 (D-474): the what-if screens. Categories 1 and 2: category 5, fr writes Version as English does; pure interpolation, base and scenario with an arrow; pure interpolation, who and when.
    "planning.scenarios.created",
    "planning.scenarios.result.kpiSub",
    "planning.scenarios.baseVersion",
    // D-645 (S23, the console's agents tab): category 5, French writes Agent,
    // Agents and Action as English does.
    "admin.agentModels.tab",
    "admin.agentModels.title",
    "admin.agentModels.colAgent",
    "admin.agentModels.colAction",
  ],
  zh: [
    ...SHELL_IDENTICAL.zh,
    ...COMMERCIAL_IDENTICAL.zh,
    ...OPERATIONS_IDENTICAL.zh,
    ...AGENTS_IDENTICAL.zh,
    // 2 — a symbol or punctuation cell, the same glyph in every language.
    "admin.accept.strength.none",
    // M15-4: an unread health figure is a dash in every language.
    "admin.health.none",
    "audit.row.noEntity",
    "catalogue.cost.none",
    "catalogue.costRun.colDelta",
    "catalogue.costRun.minDeltaOption",
    "inventory.atp.noDrill",
    "onboarding.importDetail.mapping.colState",
    "onboarding.importDetail.meta.none",
    "onboarding.importDetail.rows.messagesNone",
    "onboarding.vertical.drawer.steps.scrapPct",
    // 1 — a pure interpolation cell: the value is placeholders and separators
    // only, and the data arrives already localised.
    "inventory.atp.asOf",
    "inventory.lookup.groups.units",
    "inventory.unitCard.lastScanValue",
    "onboarding.catalogueStep.dropzone.chosen",
    "onboarding.importDetail.meta.facility",
    "onboarding.importShared.fields.facility_id",
    "onboarding.importShared.fields.work_center",
    "onboarding.plant.absentKind.department",
    "onboarding.plant.absentKind.facility",
    "onboarding.plant.absentKind.work_center",
    "onboarding.plant.bindings.facilityLabel",
    "onboarding.plant.facilityLabel",
    "onboarding.plant.people.facilityLabel",
    "onboarding.plant.resultPart",
    "onboarding.vertical.drawer.qc.range",
    // 3 — a proper noun or an acronym the glossary keeps in English in this
    // language (Assessly, VAT, SKU, CSV, PDF, MRP).
    "admin.brand",
    "onboarding.qualityReview.sampling.aql",
    // 4 — machine syntax the user types or reads verbatim.
    "admin.recovery.fileName",
    "catalogue.uom.reciprocal",
    // P3-J J-4 (D-474): the what-if screens. Categories 1 and 2: pure interpolation, base and scenario with an arrow.
    "planning.scenarios.result.kpiSub",
  ],
  "zh-Hant": [
    ...SHELL_IDENTICAL["zh-Hant"],
    ...COMMERCIAL_IDENTICAL["zh-Hant"],
    ...OPERATIONS_IDENTICAL["zh-Hant"],
    ...AGENTS_IDENTICAL["zh-Hant"],
    // 2 — a symbol or punctuation cell, the same glyph in every language.
    "admin.accept.strength.none",
    // M15-4: an unread health figure is a dash in every language.
    "admin.health.none",
    "audit.row.noEntity",
    "catalogue.cost.none",
    "catalogue.costRun.colDelta",
    "catalogue.costRun.minDeltaOption",
    "inventory.atp.noDrill",
    "onboarding.importDetail.mapping.colState",
    "onboarding.importDetail.meta.none",
    "onboarding.importDetail.rows.messagesNone",
    "onboarding.vertical.drawer.steps.scrapPct",
    // 1 — a pure interpolation cell: the value is placeholders and separators
    // only, and the data arrives already localised.
    "inventory.atp.asOf",
    "inventory.lookup.groups.units",
    "inventory.unitCard.lastScanValue",
    "onboarding.catalogueStep.dropzone.chosen",
    "onboarding.importDetail.meta.facility",
    "onboarding.importShared.fields.facility_id",
    "onboarding.importShared.fields.work_center",
    "onboarding.plant.absentKind.department",
    "onboarding.plant.absentKind.facility",
    "onboarding.plant.absentKind.work_center",
    "onboarding.plant.bindings.facilityLabel",
    "onboarding.plant.facilityLabel",
    "onboarding.plant.people.facilityLabel",
    "onboarding.plant.resultPart",
    "onboarding.vertical.drawer.qc.range",
    // 3 — a proper noun or an acronym the glossary keeps in English in this
    // language (Assessly, VAT, SKU, CSV, PDF, MRP).
    "admin.brand",
    "onboarding.qualityReview.sampling.aql",
    // 4 — machine syntax the user types or reads verbatim.
    "admin.recovery.fileName",
    "catalogue.uom.reciprocal",
    // P3-J J-4 (D-474): the what-if screens. Categories 1 and 2: pure interpolation, base and scenario with an arrow.
    "planning.scenarios.result.kpiSub",
  ],
  hi: [
    // Category 1 (D-560): the demand pool's filter chip is the filter's name
    // and its value, both already translated, with a colon.
    "planning.demand.filterChip",
    ...SHELL_IDENTICAL.hi,
    ...COMMERCIAL_IDENTICAL.hi,
    ...OPERATIONS_IDENTICAL.hi,
    ...AGENTS_IDENTICAL.hi,
    // 2 — a symbol or punctuation cell, the same glyph in every language.
    "admin.accept.strength.none",
    // M15-4: an unread health figure is a dash in every language.
    "admin.health.none",
    "audit.row.noEntity",
    "catalogue.cost.none",
    "catalogue.costRun.colDelta",
    "catalogue.costRun.minDeltaOption",
    "inventory.atp.noDrill",
    "onboarding.importDetail.mapping.colState",
    "onboarding.importDetail.meta.none",
    "onboarding.importDetail.rows.messagesNone",
    "onboarding.plant.sentence.comma",
    "onboarding.vertical.drawer.steps.scrapPct",
    // 1 — a pure interpolation cell: the value is placeholders and separators
    // only, and the data arrives already localised.
    "inventory.adjustRequest.lotPart",
    "inventory.atp.asOf",
    "inventory.lookup.groups.units",
    "inventory.unitCard.lastScanValue",
    "onboarding.catalogueStep.dropzone.chosen",
    "onboarding.importDetail.meta.facility",
    "onboarding.importShared.fields.facility_id",
    "onboarding.importShared.fields.work_center",
    "onboarding.plant.absentKind.department",
    "onboarding.plant.absentKind.facility",
    "onboarding.plant.absentKind.work_center",
    "onboarding.plant.bindings.facilityLabel",
    "onboarding.plant.facilityLabel",
    "onboarding.plant.people.facilityLabel",
    "onboarding.plant.resultPart",
    "onboarding.vertical.drawer.qc.range",
    // The FilterBar chip (P3-UX wave 4): a filter's label, a colon and its
    // value, both already in Hindi.
    "catalogue.productsList.filters.chip",
    // 3 — a proper noun or an acronym the glossary keeps in English in this
    // language (Assessly, VAT, SKU, CSV, PDF, MRP).
    "admin.brand",
    "catalogue.floorLookup.card.sku",
    "catalogue.productDetail.details.sku",
    "catalogue.productDetail.history.entityBom",
    "catalogue.productsList.col.sku",
    "catalogue.productsList.create.sku",
    "onboarding.importShared.fields.sku",
    "onboarding.qualityReview.sampling.aql",
    "public.quote.vatExclusive",
    // 4 — machine syntax the user types or reads verbatim.
    "admin.recovery.fileName",
    "catalogue.uom.reciprocal",
    // P3-J J-4 (D-474): the what-if screens. Categories 1 and 2: a percentage cell; pure interpolation, base and scenario with an arrow; pure interpolation, who and when.
    "planning.scenarios.created",
    "planning.scenarios.result.kpiSub",
    "planning.scenarios.result.pct",
  ],
};

/** The namespace a key belongs to: the longest declared prefix that matches. */
export function namespaceOf(key: string): Namespace | undefined {
  let best: Namespace | undefined;
  for (const ns of NAMESPACES) {
    if (key === ns || key.startsWith(`${ns}.`)) {
      if (best === undefined || ns.length > best.length) best = ns;
    }
  }
  return best;
}

/** Locales that claim real translations — the ones the value gate polices. */
export const TRANSLATED_LOCALES: readonly Locale[] = LOCALES.filter(
  (l) => LOCALE_COMPLETENESS[l].status === "translated",
);
